import React, {useMemo, useRef, useState} from 'react';
import MainLayout from '../../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../../theme/lightTheme';
import {getFontFamily, pickTabsByKey} from '../../../../utils/helpers';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {HomeTabItemType} from '../../../../interfaces/reducers/homeState';
import {tr} from '../../../../translations';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import {FilterTabNewsProps} from '../../../../interfaces/screens/filterTabNews';
import {FlatList} from 'react-native-gesture-handler';
import {ViewToken, Dimensions, ListRenderItem} from 'react-native';
import FilterTabNews from '../filterTabNews';
import {TabKey} from '../../../../utils/enums';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useAnimatedScrollHandler,
  useDerivedValue,
  runOnJS,
} from 'react-native-reanimated';
import GenericButton from '../../../../components/button';
import TextGeneric from '../../../../components/textGeneric';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import {RouteProp} from '@react-navigation/native';
import {LatestScreenParams} from '../../../../interfaces/screens/latest';
import {isMobile, isWeb} from '../../../../utils/responsive';
import {LinearGradient} from 'react-native-linear-gradient';

const Container = styled.ScrollView`
  flex: 1;
`;

const TabContainer = styled.View<{headerHeight: number; isHidden?: boolean}>`
  width: 100%;
  align-self: center;
  justify-content: center;
  align-items: center;
  left: 0;
  z-index: 2;
  padding-top: ${({theme}) => theme.text.s55}px;
  ${({isHidden, headerHeight}) =>
    isHidden
      ? `
      pointer-events:none;
      opacity:0;
      `
      : `
      padding-top: ${headerHeight}px;
  position: absolute;
  top:0;
  left: 0;
  `}
`;

const TabContentContainer = styled.View<{tabContainerWidth: string}>`
  width: ${({tabContainerWidth}) => tabContainerWidth};
  justify-content: space-evenly;
  align-items: center;
  flex-direction: row;
  border-width: 1px;
  border-color: ${({theme}) => theme.text.dark};
  border-radius: 100px;
  padding: ${({theme}) => theme.text.s12 / 2}px;
  position: relative;
`;

const AnimatedSlider = styled(Animated.View)`
  position: absolute;
  height: ${isWeb ? 90 : 100}%;
  width: 50%;
  background-color: ${({theme}) => theme.primary};
  border-radius: 100px;
`;

const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.dark};
  width: 90%;
  font-size: ${({theme}) => theme.text.s55}px;
  margin-bottom: ${({theme}) => theme.text.s55}px;
`;

const ItemContainer = styled(Animated.View)<{
  screenWidth: number;
}>`
  width: ${({screenWidth}) => screenWidth}px;
`;

const LIST: TabKey[] = [TabKey.LatestUpdates, TabKey.Congress];
const SPRING_CONFIG = {
  damping: 20,
  stiffness: 90,
  mass: 1,
  overshootClamping: false,
  restDisplacementThreshold: 0.01,
  restSpeedThreshold: 0.01,
};

/**
 * FilterTabNews screen component with enhanced animations.
 *
 * @param {FilterTabNewsProps} props - The screen component props.
 */
const MedicalUpdatesCongresses: React.FC<FilterTabNewsProps> = () => {
  const theme = useTheme() as Theme;
  const headerHeight = useCustomHeaderHeight();
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const {tabs} = useSelector((state: RootState) => state.homeTabs);
  const flatListRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const tabContainerWidth = getByScreenSize(wdp(90), wdp(40));
  const tabWidth = tabContainerWidth / 4.1;

  // Animated values
  const scrollX = useSharedValue(0);
  const slidePosition = useSharedValue(0);
  const screenWidth = Dimensions.get('window').width;

  // Animation for the tab indicator
  const animatedSliderStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: withSpring(
            activeIndex === 0 ? -tabWidth : tabWidth,
            SPRING_CONFIG,
          ),
        },
      ],
    };
  });

  // Scroll handler for the FlatList
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollX.value = event.contentOffset.x;
      const position = event.contentOffset.x / screenWidth;
      slidePosition.value = position > 0.5 ? 1 : 0;
    },
  });

  // Update active index from scroll position
  useDerivedValue(() => {
    const position = Math.round(scrollX.value / screenWidth);
    if (position !== activeIndex) {
      runOnJS(setActiveIndex)(position);
    }
  });

  const onViewCallBack = React.useCallback(
    ({viewableItems}: {viewableItems: Array<ViewToken>}) => {
      if (
        viewableItems &&
        viewableItems.length > 0 &&
        viewableItems[0] !== undefined
      ) {
        setActiveIndex(viewableItems[0].index || 0);
        slidePosition.value = viewableItems[0].index || 0;
      }
    },
    [],
  );

  const selectedTabs = useMemo<Record<TabKey, HomeTabItemType>>(
    () => pickTabsByKey(tabs, [TabKey.LatestUpdates, TabKey.Congress]),
    [tabs],
  );

  const renderTabItem: ListRenderItem<TabKey> = React.useCallback(
    ({item}) => {
      const params = selectedTabs[item];
      // Create a route object with the proper shape
      const route = {
        params: params as unknown as LatestScreenParams,
        key: item,
        name: 'params',
      } as RouteProp<{params: LatestScreenParams}, 'params'>;

      return (
        <ItemContainer key={item} screenWidth={wdp(100)}>
          <FilterTabNews
            isComponent
            topComponent={_renderTopTaps}
            //@ts-ignore
            route={route}
          />
        </ItemContainer>
      );
    },
    [selectedTabs, wdp, hdp],
  );

  const scrollTab = React.useCallback(
    (index: number) => {
      if (index === activeIndex) {
        return;
      }

      slidePosition.value = index;

      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
        });
      }
    },
    [activeIndex, slidePosition],
  );

  const _renderTopTaps = React.useCallback(
    (isVisible?: boolean) => {
      return (
        <TabContainer
          headerHeight={headerHeight + hdp(2)}
          isHidden={!isVisible}>
          <Title
            style={{
              textAlign: isMobile ? 'left' : 'center',
            }}
            semiBold>
            {tr('tabNews.tabTitle')}
          </Title>
          <TabContentContainer tabContainerWidth={tabContainerWidth}>
            <AnimatedSlider style={animatedSliderStyle} />
            {LIST.map((type, idx) => (
              <GenericButton
                isSmall={getByScreenSize(false, true)}
                key={idx}
                label={selectedTabs[type]?.tab_name || tr(`tabNews.${type}`)}
                labelStyle={{
                  color:
                    activeIndex === idx ? theme.text.light : theme.text.dark,
                  fontFamily: getFontFamily().bold,
                }}
                style={{
                  width: '49.5%',
                  borderRadius: 100,
                  overflow: 'hidden',
                  backgroundColor: 'transparent',
                }}
                radius={100}
                onPress={() => scrollTab(idx)}
              />
            ))}
          </TabContentContainer>
        </TabContainer>
      );
    },
    [
      activeIndex,
      headerHeight,
      hdp,
      selectedTabs,
      theme,
      animatedSliderStyle,
      tabContainerWidth,
      scrollTab,
    ],
  );

  return (
    <MainLayout
      backHeader
      menuHeader
      noPadding
      title={tr('home.medicalUpdates')}
      noPaddingHeader>
      <Container>
        <LinearGradient
          colors={[
            theme.tabNews.startLinearGradient,
            theme.tabNews.secondLinearGradient,
            theme.tabNews.thirdLinearGradient,
          ]}
          start={{x: 0, y: 0}}
          end={{x: 1, y: 1}}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
          }}
        />
        {_renderTopTaps(true)}
        <Animated.FlatList
          data={LIST}
          ref={flatListRef}
          //@ts-ignore
          renderItem={renderTabItem}
          keyExtractor={item => item + ''}
          horizontal
          // pagingEnabled
          contentContainerStyle={{
            flexGrow: 1,
          }}
          scrollEnabled
          nestedScrollEnabled
          initialScrollIndex={0}
          showsHorizontalScrollIndicator={false}
          viewabilityConfig={{viewAreaCoveragePercentThreshold: 50}}
          onViewableItemsChanged={onViewCallBack}
          getItemLayout={(data, index) => ({
            length: wdp(100),
            offset: wdp(100) * index,
            index,
          })}
          onScroll={scrollHandler}
          // performance
          initialNumToRender={1}
          maxToRenderPerBatch={2}
          removeClippedSubviews
          windowSize={2}
          scrollEventThrottle={16}
        />
      </Container>
    </MainLayout>
  );
};

export default MedicalUpdatesCongresses;
